import { ApolloError } from '@apollo/client'
import { DocumentNode } from 'graphql'
import { QueryHookOptions, QueryResult, useQuery } from '@apollo/client'
import { Data, DataPoint } from 'components/Charts/types'
import { Filter } from 'typings/filters'
import { Segment as SegmentEnum } from 'typings/segments'
import { getPercent } from 'util/math'
import { useAppliedFiltersAndContext } from 'hooks/useAppliedFiltersAndContext'
import { SampleSizeState } from 'hooks/useAudienceSegments/utils'

export const SEGMENT_VARIABLE_NAME = 'shopperClusterSegmentation'

export type DataAccessor = KeysWithValsOfType<Required<Partial<DataPoint>>, number>

export interface SegmentKPIResult {
  data?: Partial<DataPoint>
  loading: boolean
  error?: ApolloError
}

export interface SegmentKPIs {
  segment: SegmentEnum
  sampleSizeState: SampleSizeState
  kpis: SegmentKPIResult[]
}

export interface SegmentData {
  loading: boolean
  summary: SegmentKPIResult[]
  segments: SegmentKPIs[]
}

export interface Options {
  queryDocument: DocumentNode
  crossVar?: string
  filters: Pick<Filter, 'variable_id' | 'answer_code'>[]
  dataAccessor: DataAccessor
  hintText?: string
  skip?: boolean
}

interface Segment {
  segment: SegmentEnum
  sampleSizeState: SampleSizeState
}

export const getAverage = (dataPoint?: DataPoint) => {
  if (dataPoint?.average) {
    return parseFloat(dataPoint.average.toFixed(2))
  }
  return 0
}

const mapKPITotals = (
  kpiResults: QueryResult<Data>[],
  totalsResult: QueryResult<Data>
): SegmentKPIResult[] => {
  const totalCount = totalsResult.data?.cross.totalCount
  return kpiResults.map((kpiResult) => {
    const { loading, error, data } = kpiResult
    const kpiTotal = data?.cross.totalCount

    if (totalCount && kpiTotal) {
      const percent = getPercent(kpiTotal, totalCount)
      const average = getAverage(data?.cross.summarySeries.dataPoints[0])
      return {
        loading,
        error,
        data: {
          weighted: data?.cross.summarySeries.seriesTotal, // Normalize totals result for when dataAccessor is `weighted`
          unweighted: data?.cross.summarySeries.seriesTotalUnweighted,
          percent,
          marketShare: percent,
          average,
        },
      }
    }

    return { loading, error }
  })
}

const mapKPISegments = (kpiResults: QueryResult<Data>[], segments: Segment[]): SegmentKPIs[] =>
  segments.map(({ segment, sampleSizeState }) => {
    return {
      segment,
      sampleSizeState,
      kpis: kpiResults.map(({ loading, error, data }) => {
        // Find the data that matches the given segment in this KPI result's summarySeries
        let dataPoint = data?.cross.summarySeries?.dataPoints.find(
          ({ secondaryCode }) => segment === secondaryCode
        )

        if (!dataPoint) {
          dataPoint = data?.cross.series.find((s) => s.primaryCode === segment)?.dataPoints[0]
        }

        return {
          error,
          loading,
          data: dataPoint,
        }
      }),
    }
  })

export const useSegmentLeaderboardKPIs = <N extends number = never>(
  segments: Segment[],
  options: Tuple<Options, N>,
  totalBaseOptions: Options
): SegmentData => {
  const { appliedFilters, context } = useAppliedFiltersAndContext()

  const makeQueryArgs = (queryOptions: Options): [DocumentNode, QueryHookOptions] => {
    // Answers contain variable identifiers on each data point, so if this
    // is a solitary query, the segment variable should be the primary variable;
    // otherwise, it must be the secondary variable
    const variables: QueryHookOptions['variables'] = {
      primaryVar: SEGMENT_VARIABLE_NAME,
      secondaryVar: queryOptions.crossVar ?? '',
      filters: appliedFilters,
      localFilters: queryOptions.filters,
      context,
    }

    return [queryOptions.queryDocument, { variables, skip: queryOptions.skip }]
  }

  const totalsResult = useQuery<Data>(...makeQueryArgs(totalBaseOptions))

  // "react-hooks/rules-of-hooks" rule can be disabled as far as the number of options do not change.
  // Make sure to set the number of Options N for "useSegmentLeaderboardKPIs"
  // when using this hook.
  const kpiResults = options.map((queryOptions) => useQuery<Data>(...makeQueryArgs(queryOptions)))

  return {
    loading: totalsResult.loading || kpiResults.some(({ loading }) => loading),
    summary: mapKPITotals(kpiResults, totalsResult),
    segments: mapKPISegments(kpiResults, segments),
  }
}
